import { z } from 'zod';
import { financeMoneyCentsSchema } from './finance-invoice.contract.js';
export const financeReferralCommissionCapability = 'finance.referral-commissions.v1';
export const financeReferralCommissionCostsCapability = 'finance.referral-commission-costs.v1';
export const financeReferralCommissionAttributionsCapability = 'relationships.finance-commission-attributions.v1';
export const financeReferralCommissionAttributionsSlot = 'finance.referral-commission-attributions';
export const financeReferralCommissionAttributionsProviderId = 'relationships.server.finance-commission-attributions';
export const financeReferralCommissionIdSchema = z.string().uuid();
export const financeReferralCommissionRatePartsPerMillionSchema = z.number().int().min(0).max(1_000_000);
export const financeReferralCommissionScopeSchema = z.enum([
    'tenant-default', 'referral-source', 'membership',
]);
export const financeReferralCommissionPayoutTypeSchema = z.enum(['percent', 'flat', 'none']);
export const financeReferralCommissionBasisSchema = z.enum([
    'revenue', 'gross-margin', 'net-profit', 'estimated-net-profit',
]);
export const financeReferralCommissionPayoutStatusSchema = z.enum([
    'estimated', 'accrued', 'pending-payment', 'ready-for-approval',
    'approved', 'paid', 'locked', 'void', 'clawback-pending',
]);
export const financeReferralCommissionAttributionFactSchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'), projectId: financeReferralCommissionIdSchema,
        attributionId: financeReferralCommissionIdSchema,
        referralSourceId: financeReferralCommissionIdSchema.nullable(),
        creditedMembershipId: financeReferralCommissionIdSchema.nullable(),
        creditSource: z.enum(['lead-assignment', 'uncredited']),
        bookedAt: z.string().datetime({ offset: true }),
        observedAt: z.string().datetime({ offset: true }),
    }),
    z.strictObject({
        state: z.literal('not-attributed'), projectId: financeReferralCommissionIdSchema,
    }),
    z.strictObject({
        state: z.literal('ambiguous'), projectId: financeReferralCommissionIdSchema,
        attributionCount: z.number().int().min(2).max(100),
    }),
]);
export const financeReferralCommissionAttributionsResponseSchema = z.strictObject({
    contractVersion: z.literal(financeReferralCommissionAttributionsCapability),
    tenantId: financeReferralCommissionIdSchema,
    attributions: z.array(financeReferralCommissionAttributionFactSchema).min(1).max(100),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    observedAt: z.string().datetime({ offset: true }).nullable(),
});
export const financeReferralCommissionSplitSchema = z.strictObject({
    creditedMembershipId: financeReferralCommissionIdSchema.nullable(),
    allocationPartsPerMillion: financeReferralCommissionRatePartsPerMillionSchema.min(1),
});
export const financeReferralCommissionRuleValueSchema = z.strictObject({
    name: z.string().trim().min(1).max(200),
    scope: financeReferralCommissionScopeSchema,
    scopeId: financeReferralCommissionIdSchema.nullable(),
    payoutType: financeReferralCommissionPayoutTypeSchema,
    basis: financeReferralCommissionBasisSchema,
    payoutRatePartsPerMillion: financeReferralCommissionRatePartsPerMillionSchema.nullable(),
    flatAmountCents: financeMoneyCentsSchema.nullable(),
    estimatedNetProfitPartsPerMillion: financeReferralCommissionRatePartsPerMillionSchema,
    holdDays: z.number().int().min(0).max(365),
    managerApprovalRequired: z.boolean(),
    autoExpenseEnabled: z.boolean(),
    splits: z.array(financeReferralCommissionSplitSchema).max(12),
    active: z.boolean(),
}).superRefine((value, context) => {
    if ((value.scope === 'tenant-default') !== (value.scopeId === null)) {
        context.addIssue({
            code: 'custom', path: ['scopeId'],
            message: 'Only a tenant-default rule omits its scope identity.',
        });
    }
    if ((value.payoutType === 'percent') !== (value.payoutRatePartsPerMillion !== null)) {
        context.addIssue({
            code: 'custom', path: ['payoutRatePartsPerMillion'],
            message: 'Only a percent rule carries a payout rate.',
        });
    }
    if ((value.payoutType === 'flat') !== (value.flatAmountCents !== null)) {
        context.addIssue({
            code: 'custom', path: ['flatAmountCents'],
            message: 'Only a flat rule carries a flat amount.',
        });
    }
    if (value.splits.length > 0) {
        const total = value.splits.reduce((sum, split) => sum + split.allocationPartsPerMillion, 0);
        if (total !== 1_000_000) {
            context.addIssue({
                code: 'custom', path: ['splits'],
                message: 'Commission splits must allocate exactly 1,000,000 parts per million.',
            });
        }
        const identities = value.splits.map(({ creditedMembershipId }) => creditedMembershipId);
        if (new Set(identities).size !== identities.length) {
            context.addIssue({
                code: 'custom', path: ['splits'], message: 'Commission split recipients cannot repeat.',
            });
        }
    }
});
export const financeReferralCommissionRuleSchema = z.strictObject({
    contractVersion: z.literal(financeReferralCommissionCapability),
    tenantId: financeReferralCommissionIdSchema,
    id: financeReferralCommissionIdSchema,
    revision: z.number().int().positive(),
    currentVersionId: financeReferralCommissionIdSchema,
    value: financeReferralCommissionRuleValueSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
const commandEnvelope = {
    commandId: financeReferralCommissionIdSchema,
    reason: z.string().trim().min(1).max(2_000),
};
export const financeReferralCommissionRuleCommandSchema = z.strictObject({
    ...commandEnvelope,
    ruleId: financeReferralCommissionIdSchema,
    expectedRevision: z.number().int().nonnegative(),
    value: financeReferralCommissionRuleValueSchema,
});
export const financeReferralCommissionCalculationCommandSchema = z.strictObject({
    ...commandEnvelope,
    projectId: financeReferralCommissionIdSchema,
    explicitRuleId: financeReferralCommissionIdSchema.nullable().default(null),
    actualBasisAmountCents: financeMoneyCentsSchema.nullable().default(null),
    forceRecalculation: z.boolean().default(false),
});
export const financeReferralCommissionTransitionSchema = z.enum([
    'accrue', 'queue-payment', 'submit-approval', 'approve', 'mark-paid',
    'lock', 'void', 'request-clawback',
]);
export const financeReferralCommissionPayoutCommandSchema = z.strictObject({
    ...commandEnvelope,
    payoutId: financeReferralCommissionIdSchema,
    expectedRevision: z.number().int().positive(),
    transition: financeReferralCommissionTransitionSchema,
    paidAt: z.string().datetime({ offset: true }).nullable().default(null),
});
export const financeReferralCommissionPayoutSchema = z.strictObject({
    contractVersion: z.literal(financeReferralCommissionCapability),
    tenantId: financeReferralCommissionIdSchema,
    id: financeReferralCommissionIdSchema,
    generationId: financeReferralCommissionIdSchema,
    projectId: financeReferralCommissionIdSchema,
    attributionId: financeReferralCommissionIdSchema.nullable(),
    referralSourceId: financeReferralCommissionIdSchema.nullable(),
    creditedMembershipId: financeReferralCommissionIdSchema.nullable(),
    ruleId: financeReferralCommissionIdSchema,
    ruleVersionId: financeReferralCommissionIdSchema,
    revision: z.number().int().positive(),
    status: financeReferralCommissionPayoutStatusSchema,
    basis: financeReferralCommissionBasisSchema,
    basisAmountCents: financeMoneyCentsSchema,
    payoutAmountCents: financeMoneyCentsSchema,
    currencyCode: z.literal('USD'),
    holdUntil: z.string().date(),
    managerApprovalRequired: z.boolean(),
    autoExpenseEnabled: z.boolean(),
    linkedExpenseId: financeReferralCommissionIdSchema.nullable(),
    supersededByGenerationId: financeReferralCommissionIdSchema.nullable(),
    paidAt: z.string().datetime({ offset: true }).nullable(),
    lockedAt: z.string().datetime({ offset: true }).nullable(),
    voidedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
const financeReferralCommissionPageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
});
export const financeReferralCommissionRuleListResponseSchema = z.strictObject({
    data: z.array(financeReferralCommissionRuleSchema),
    page: financeReferralCommissionPageSchema,
});
export const financeReferralCommissionPayoutListResponseSchema = z.strictObject({
    data: z.array(financeReferralCommissionPayoutSchema),
    page: financeReferralCommissionPageSchema,
});
export const financeReferralCommissionCommandResponseSchema = z.strictObject({
    data: z.array(financeReferralCommissionPayoutSchema).max(12),
    command: z.strictObject({
        commandId: financeReferralCommissionIdSchema,
        idempotent: z.boolean(),
        outcome: z.enum(['calculated', 'no-rule', 'not-attributed']),
    }),
});
export const financeReferralCommissionRuleCommandResponseSchema = z.strictObject({
    data: financeReferralCommissionRuleSchema,
    command: z.strictObject({
        commandId: financeReferralCommissionIdSchema,
        idempotent: z.boolean(),
    }),
});
export const financeReferralCommissionPayoutCommandResponseSchema = z.strictObject({
    data: financeReferralCommissionPayoutSchema,
    command: z.strictObject({
        commandId: financeReferralCommissionIdSchema,
        idempotent: z.boolean(),
    }),
});
export const financeReferralCommissionListQuerySchema = z.strictObject({
    projectId: financeReferralCommissionIdSchema.optional(),
    status: financeReferralCommissionPayoutStatusSchema.optional(),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().min(0).max(1_000_000).default(0),
});
function roundPartsPerMillion(amountCents, partsPerMillion) {
    const amount = BigInt(amountCents);
    const parts = BigInt(partsPerMillion);
    const numerator = amount * parts;
    const magnitude = numerator < 0n ? -numerator : numerator;
    const rounded = (magnitude + 500000n) / 1000000n;
    return Number(numerator < 0n ? -rounded : rounded);
}
function splitAmount(payoutAmountCents, splits) {
    const sign = payoutAmountCents < 0 ? -1 : 1;
    const magnitude = BigInt(Math.abs(payoutAmountCents));
    const prepared = splits.map((split, index) => {
        const numerator = magnitude * BigInt(split.allocationPartsPerMillion);
        return {
            ...split,
            index,
            base: numerator / 1000000n,
            remainder: numerator % 1000000n,
        };
    });
    let remaining = magnitude - prepared.reduce((sum, split) => sum + split.base, 0n);
    const order = [...prepared].sort((left, right) => {
        if (left.remainder !== right.remainder)
            return left.remainder > right.remainder ? -1 : 1;
        const leftId = left.creditedMembershipId ?? '';
        const rightId = right.creditedMembershipId ?? '';
        return leftId.localeCompare(rightId) || left.index - right.index;
    });
    const extras = new Set();
    for (const split of order) {
        if (remaining === 0n)
            break;
        extras.add(split.index);
        remaining -= 1n;
    }
    return prepared.map((split) => ({
        creditedMembershipId: split.creditedMembershipId,
        allocationPartsPerMillion: split.allocationPartsPerMillion,
        payoutAmountCents: Number(split.base + (extras.has(split.index) ? 1n : 0n)) * sign,
    }));
}
export function calculateFinanceReferralCommission(input) {
    const value = financeReferralCommissionRuleValueSchema.parse(input.rule.value);
    const revenueCents = financeMoneyCentsSchema.parse(input.revenueCents);
    const actualBasis = input.actualBasisAmountCents === null
        ? null
        : financeMoneyCentsSchema.parse(input.actualBasisAmountCents);
    let basisAmountCents;
    if (value.basis === 'revenue')
        basisAmountCents = revenueCents;
    else if (value.basis === 'estimated-net-profit') {
        basisAmountCents = roundPartsPerMillion(revenueCents, value.estimatedNetProfitPartsPerMillion);
    }
    else {
        if (actualBasis === null) {
            throw new Error(`Finance commission ${value.basis} requires an exact actual basis amount.`);
        }
        basisAmountCents = actualBasis;
    }
    const payoutAmountCents = value.payoutType === 'percent'
        ? roundPartsPerMillion(basisAmountCents, value.payoutRatePartsPerMillion)
        : value.payoutType === 'flat'
            ? value.flatAmountCents
            : 0;
    const configuredSplits = value.splits.length > 0
        ? value.splits
        : [{
                creditedMembershipId: input.attribution.creditedMembershipId,
                allocationPartsPerMillion: 1_000_000,
            }];
    const splits = splitAmount(payoutAmountCents, configuredSplits);
    if (splits.reduce((sum, split) => sum + split.payoutAmountCents, 0) !== payoutAmountCents) {
        throw new Error('Finance commission splits did not reconcile exactly.');
    }
    return { basisAmountCents, payoutAmountCents, splits };
}
export function selectFinanceReferralCommissionRule(rules, attribution, explicitRuleId) {
    const active = rules.filter(({ value }) => value.active);
    if (explicitRuleId !== null)
        return active.find(({ id }) => id === explicitRuleId) ?? null;
    return active.find(({ value }) => value.scope === 'membership' && value.scopeId === attribution.creditedMembershipId) ??
        active.find(({ value }) => value.scope === 'referral-source' && value.scopeId === attribution.referralSourceId) ??
        active.find(({ value }) => value.scope === 'tenant-default') ??
        null;
}
//# sourceMappingURL=finance-referral-commission.contract.js.map